// Interfaces
import User from "../core/interfaces/User";
import Tag from "../core/interfaces/Tag";
import SelectItem from "../core/interfaces/SelectItem";
import { CategoryJsonPage } from "../core/interfaces/JsonPage";
import {
  EventSearchDefaults,
  validatedEventSearchDefaults,
  validateProductAreaProperties,
} from "../core/types/EventSearchDefaults";

// Components to use
import { CCCPanel } from "../components/panel/panel";
import "../components/search-panel/searchPanel";
import { CCCSearchPanel } from "../components/search-panel/searchPanel";
import { CCCInputText } from "../components/forms/input-text/index";
import { CCCSelectMany } from "../components/forms/select-many/selectMany";
import SelectManyVM from "../components/forms/select-many/selectManyVm";
import { CCCDatePicker } from "../components/forms/date-picker";

// Models
import CategoryModel, { CategoryType } from "../models/CategoryModel";
import IdeaModel from "../models/IdeaModel";
import UserModel from "../models/UserModel";
import OContentModel from "../models/OContentModel";
import { CCCSelectSingle } from "../components/forms/select-single";

export default class SearchPanelController {
  private $panel: CCCPanel = null;
  private $component: CCCSearchPanel = null;
  private $ideaNumber: CCCInputText = null;
  private $keywords: CCCInputText = null;
  private $dateFrom: CCCDatePicker = null;
  private $dateTo: CCCDatePicker = null;
  private $author: CCCSelectMany = null;
  private $statuses: CCCSelectMany = null;
  private $tags: CCCSelectMany = null;
  private categoryMetadata: CategoryJsonPage;
  private categoryType: CategoryType;
  private eventSearchDefaults: EventSearchDefaults;
  private allCategoryTags: Array<Tag> = [];
  private filteredCategoryTags: Array<Tag> = [];
  //Add variable for product areas
  private $productAreas: CCCSelectSingle = null;
  private chosenArea: string = null;

  constructor(anchor: string) {
    this.$panel = document.querySelector(anchor);
    this.$component = document.querySelector("ccc-search-panel");
  }

  /**
   * Initializes the search component.
   */
  public async init(): Promise<void> {
    console.debug("Search Panel - INIT");
    if ((this.$panel || this.$component) && (await this.shouldDisplay())) {
      await this.initVariables();
      this.displaySearchPanel();
      // Override onRawValueChange event on Author field to get users and update state.
      if (this.$author) {
        this.$author.onRawValueChange(this.onRawValueChange, 500);
        this.$author.onFocusChange(null);
      }
      // Add event to "Search" button.
      this.$component.onSearch(this.onSearch);
    } else {
      this.removeSearchComponent();
    }
  }

  private async shouldDisplay(): Promise<boolean> {
    try {
      this.categoryType = await CategoryModel.getCategoryTypeCached();
      console.debug("Search Panel - Category type:", this.categoryType);
      return (
        this.categoryType === CategoryType.allEvents ||
        this.categoryType === CategoryType.events ||
        this.categoryType === CategoryType.discussions ||
        this.categoryType === CategoryType.ideas
      );
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  /**
   * Initialize variables.
   */
  private async initVariables(): Promise<void> {
    // Initialize web components.
    this.$ideaNumber =
      this.$component.shadowDom.querySelector("#ideaNumberFld");
    this.$keywords = this.$component.shadowDom.querySelector("#keywordsFld");
    this.$dateFrom = this.$component.shadowDom.querySelector("#dateFromFld");
    this.$dateTo = this.$component.shadowDom.querySelector("#dateToFld");
    this.$author = this.$component.shadowDom.querySelector("#authorsFld");
    this.$statuses = this.$component.shadowDom.querySelector("#statusesFld");
    this.$tags = this.$component.shadowDom.querySelector("#tagsFld");
    //Init product areas variable
    this.$productAreas =
      this.$component.shadowDom.querySelector("#productAreaSelect");
    // Get category information.
    try {
      this.categoryMetadata =
        await CategoryModel.getCategoryFromJsonPageCached();
      console.debug("Search Panel - Category metadata:", this.categoryMetadata);
    } catch (error) {
      console.error("Search Panel - Error getting category information");
      this.removeSearchComponent();
    }
  }

  /**
   * Sets category type according to page metadata, updates category tags and shows the panel.
   */
  private displaySearchPanel(): void {
    this.setPanelType(this.categoryType);
    // Set tags field up.
    CategoryModel.getCategoryTags()
      .then((tags: Array<Tag>) => {
        console.debug("Search Panel - Category tags:", tags);
        if (tags && tags.length > 0) {
          this.allCategoryTags = tags;
          tags.forEach((tag) => {
            const exist = this.filteredCategoryTags.some(
              (filteredCategoryTag) => tag.name === filteredCategoryTag.name
            );
            if (!exist) {
              this.filteredCategoryTags.push(tag);
            }
          });
          this.updateTags(this.filteredCategoryTags);
        } else {
          throw "Search Panel - Category doesn't have tags";
        }
      })
      .catch((error) => {
        this.$tags.parentNode.removeChild(this.$tags);
        console.error(error);
      })
      .finally(() => {
        this.$component.loading = false;
      });
  }

  /**
   * Updates panel according to the category type.
   * @param {CategoryType} categoryType
   */
  private setPanelType(categoryType: CategoryType): void {
    switch (categoryType) {
      case CategoryType.allEvents: {
        this.$productAreas.remove();
        this.$panel.setAttribute("title", "Search Events");
        this.$component.setAttribute("type", "events");
        break;
      }
      case CategoryType.events: {
        this.$panel.setAttribute("title", "Search Events");
        this.$component.setAttribute("type", "events");
        // Get defaults.
        OContentModel.getEventSearchDefaults()
          .then((eventSearchDefaultsMap) => {
            const eventSearchDefaults = eventSearchDefaultsMap.find(
              (cat) => cat.categoryId === this.categoryMetadata.CategoryID
            );
            if (validatedEventSearchDefaults(eventSearchDefaults)) {
              this.eventSearchDefaults = eventSearchDefaults;
              // Set default authors.
              if (
                this.eventSearchDefaults.defaultAuthors &&
                this.eventSearchDefaults.defaultAuthors.length > 0
              ) {
                this.eventSearchDefaults.defaultAuthors.forEach((author) => {
                  UserModel.getUsersByName(author)
                    .then((users) => {
                      if (users.length > 0) {
                        this.$author.addItem(users[0]);
                      }
                    })
                    .catch((error) => {
                      console.error(
                        "Search Panel - Error getting user called " + author,
                        error
                      );
                    });
                });
              }
              // Set default dates.
              const date = new Date();
              date.setFullYear(date.getFullYear() - 2);
              const year = new Intl.DateTimeFormat("en", {
                year: "numeric",
              }).format(date);
              const month = new Intl.DateTimeFormat("en", {
                month: "2-digit",
              }).format(date);
              const day = new Intl.DateTimeFormat("en", {
                day: "2-digit",
              }).format(date);
              this.$dateFrom.value = `${year}-${month}-${day}`;
              this.productAreaSetup();
            } else {
              console.error(
                "Search Panel - Events category doesn't have defaults."
              );
              this.removeSearchComponent();
            }
          })
          .catch((error) => {
            console.error(
              "Search Panel - Error getting events search defaults.",
              error
            );
            this.removeSearchComponent();
          });
        break;
      }
      case CategoryType.ideas: {
        this.$productAreas.remove();
        this.$panel.setAttribute("title", "Search this Idea Lab");
        this.$component.setAttribute("type", "ideas");
        this.$component.subscribeToIdeaNumberVal(this.subscribeToIdeaNumberVal);
        IdeaModel.getIdeaStatusesCached()
          .then((ideaStatuses) => {
            this.updateStatuses(ideaStatuses);
          })
          .catch(() => {
            this.$statuses.parentNode.removeChild(this.$statuses);
          });
        break;
      }
      case CategoryType.discussions: {
        this.$productAreas.remove();
        this.$panel.setAttribute("title", "Search this Category");
        this.$component.setAttribute("type", "discussions");
        break;
      }
    }
  }

  private productAreaSetup() {
    console.debug("Search Panel - Setting up product area selector...");
    if (validateProductAreaProperties(this.eventSearchDefaults)) {
      console.debug(
        "Search Panel - Needed properties for product area exist in category!"
      );
      this.$productAreas.setKeyState(
        "options",
        this.eventSearchDefaults.productAreas
      );
    } else {
      this.$productAreas.remove();
    }

    this.$productAreas.onItemSelect((prdArea) => {
      this.chosenArea = `${prdArea.name}:`;
      console.debug("Search Panel - Product Area", this.chosenArea);
    });
  }

  private removeSearchComponent() {
    console.debug("Search Panel - Should not display panel");
    if (this.$panel) {
      this.$panel.remove();
    } else if (this.$component) {
      this.$component.remove();
    }
  }

  private subscribeToIdeaNumberVal = (val: string) => {
    if (val.length > 0) {
      this.$keywords ? this.$keywords.setAttribute("disabled", "") : "";
      this.$keywords.setValue("");
      this.$dateFrom ? this.$dateFrom.setAttribute("disabled", "") : "";
      this.$dateFrom.value = "";
      this.$dateTo ? this.$dateTo.setAttribute("disabled", "") : "";
      this.$dateTo.value = "";
      this.$author ? this.$author.setAttribute("disabled", "") : "";
      const authors = this.$author.getValue();
      authors.forEach((author) => {
        this.$author.removeItem(author);
      });
      this.$statuses ? this.$statuses.setAttribute("disabled", "") : "";
      const statuses = this.$statuses.getValue();
      statuses.forEach((status) => {
        this.$statuses.removeItem(status);
      });
      this.$tags ? this.$tags.setAttribute("disabled", "") : "";
      const tags = this.$tags.getValue();
      tags.forEach((tag) => {
        this.$tags.removeItem(tag);
      });
    } else {
      this.$keywords ? this.$keywords.removeAttribute("disabled") : "";
      this.$dateFrom ? this.$dateFrom.removeAttribute("disabled") : "";
      this.$dateTo ? this.$dateTo.removeAttribute("disabled") : "";
      this.$author ? this.$author.removeAttribute("disabled") : "";
      this.$statuses ? this.$statuses.removeAttribute("disabled") : "";
      this.$tags ? this.$tags.removeAttribute("disabled") : "";
    }
  };

  private onRawValueChange = (
    selectManyVM: SelectManyVM,
    val: string
  ): void => {
    if (val.length >= parseInt(this.$author.getAttribute("min-length"))) {
      this.$author.setAttribute("internal-loading", "");
      UserModel.getUsersByName(val + "*")
        .then((users: Array<User>) => {
          console.debug("Search Panel - Users:", users);
          this.updateUsers(users);
          selectManyVM.showOptionsList();
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.$author.removeAttribute("internal-loading");
        });
    } else {
      this.updateUsers([]);
      selectManyVM.hideOptionsList();
    }
  };

  private onSearch = async (): Promise<void> => {
    const $submitBtn = this.$component.shadowDom.querySelector("#searchBtn");
    $submitBtn.setAttribute("loading", "");
    const queryParams: Array<string> = [];
    let keywords = "";
    // It's an Idea Lab
    if (
      this.categoryType === CategoryType.ideas &&
      this.$ideaNumber &&
      this.$ideaNumber.getValue()
    ) {
      if (this.$ideaNumber.getValue().length === 10) {
        keywords = '"' + encodeURI(this.$ideaNumber.getValue()) + '"';
      } else {
        const url =
          process.env.VANILLA_COMMUNITY_URL +
          "discussion/" +
          this.$ideaNumber.getValue();
        window.location.href = encodeURI(url);
        return;
      }
    }
    // Set the category filter.
    queryParams.push(
      "categoryOptions[0][value]=" + this.categoryMetadata.Category.CategoryID
    );
    queryParams.push(
      "categoryOptions[0][label]=" + this.categoryMetadata.Category.Name
    );
    queryParams.push(
      "categoryIDs[0]=" + this.categoryMetadata.Category.CategoryID
    );
    queryParams.push("categoryOptions[0][data][parentLabel]=Home");
    // It's an events category.
    if (this.categoryType === CategoryType.events) {
      if (validatedEventSearchDefaults(this.eventSearchDefaults)) {
        if (this.eventSearchDefaults.defaultTitle || this.chosenArea) {
          const nameParamValue = this.createNameQueryParam();
          queryParams.push(`name=${nameParamValue}`);
        }
        if (this.eventSearchDefaults.defaultKeywords) {
          keywords = encodeURIComponent(
            this.eventSearchDefaults.defaultKeywords
          );
        }
      }
    }
    // Set query filter.
    if (this.$keywords.getValue()) {
      keywords += encodeURIComponent(
        (keywords.length > 0 ? "+" : "") + this.$keywords.getValue()
      );
    }
    if (keywords.length > 0) {
      queryParams.push("query=" + keywords);
    }
    if (
      this.categoryType === CategoryType.allEvents ||
      this.categoryType === CategoryType.events
    ) {
      queryParams.push("includePastEvents=true");
    }
    if (this.$dateFrom.value) {
      if (
        this.categoryType === CategoryType.allEvents ||
        this.categoryType === CategoryType.events
      ) {
        queryParams.push("dateStarts=>=" + encodeURI(this.$dateFrom.value));
      } else if (
        this.categoryType === CategoryType.ideas ||
        this.categoryType === CategoryType.discussions
      ) {
        queryParams.push("startDateUpdated=" + encodeURI(this.$dateFrom.value));
        queryParams.push("startDate=" + encodeURI(this.$dateFrom.value));
      } else {
        queryParams.push("startDate=" + encodeURI(this.$dateFrom.value));
      }
    }
    if (this.$dateTo.value) {
      if (
        this.categoryType === CategoryType.allEvents ||
        this.categoryType === CategoryType.events
      ) {
        queryParams.push("dateEnds=<=" + encodeURI(this.$dateTo.value));
      } else if (
        this.categoryType === CategoryType.ideas ||
        this.categoryType === CategoryType.discussions
      ) {
        queryParams.push("endDateUpdated=" + encodeURI(this.$dateTo.value));
        queryParams.push("endDate=" + encodeURI(this.$dateTo.value));
      } else {
        queryParams.push("endDate=" + encodeURI(this.$dateTo.value));
      }
    }
    if (this.$author.getValue()) {
      const authors: Array<User> = this.$author.getValue();
      authors.forEach((author: User, id: number) => {
        queryParams.push("authors[" + id + "][value]=" + author.id);
        queryParams.push("authors[" + id + "][label]=" + author.name);
      });
    }
    let tagsCount = 0;
    if (this.$statuses.getValue()) {
      const statuses: Array<SelectItem> = this.$statuses.getValue();
      statuses.forEach((status: SelectItem, id: number) => {
        queryParams.push("tagsOptions[" + id + "][value]=" + status.id);
        queryParams.push("tagsOptions[" + id + "][label]=" + status.name);
        queryParams.push("tagsOptions[" + id + "][tagCode]=" + status.urlCode);
      });
      tagsCount = statuses.length;
    }
    if (this.$tags.getValue()) {
      const tags: Array<Tag> = this.$tags.getValue();
      tags.forEach((tag: Tag) => {
        const repeatedTags = this.allCategoryTags.filter(
          (categoryTag) => tag.name === categoryTag.name
        );
        repeatedTags.forEach((repeatedTag: Tag) => {
          queryParams.push(
            "tagsOptions[" + tagsCount + "][value]=" + repeatedTag.id
          );
          queryParams.push(
            "tagsOptions[" + tagsCount + "][label]=" + repeatedTag.name
          );
          queryParams.push(
            "tagsOptions[" + tagsCount + "][tagCode]=" + repeatedTag.urlCode
          );
          tagsCount++;
        });
      });
    }

    // Add includeChildCategories always
    queryParams.push("includeChildCategories=true");

    const url =
      process.env.VANILLA_COMMUNITY_URL +
      "search?domain=" +
      (this.categoryType === CategoryType.allEvents ||
      this.categoryType === CategoryType.events
        ? "event"
        : "discussions") +
      "&" +
      queryParams.join("&");
    window.location.href = url;
  };

  private updateUsers(value: Array<User>): void {
    this.$author.setKeyState("options", value);
  }

  private updateStatuses(value: Array<Tag>): void {
    this.$statuses.setKeyState("options", value);
  }

  private updateTags(value: Array<Tag>): void {
    this.$tags.setKeyState("options", value);
  }

  private createNameQueryParam(): string {
    console.debug("Search Panel - Creating name query param...");
    if (this.eventSearchDefaults.defaultTitle) {
      return this.chosenArea
        ? `${this.chosenArea}+${this.eventSearchDefaults.defaultTitle}`
        : this.eventSearchDefaults.defaultTitle;
    } else {
      return this.chosenArea;
    }
  }
}
